import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useCartStore } from '@/stores/cartStore';
import { calculateTotals, DEFAULT_QUOTE_CONFIG } from '@/lib/quoteMath';
import { newQuoteId, nextQuoteNumber, saveQuote } from '@/lib/quote';
import LineItemRow from '@/components/quote/LineItemRow';
import QuoteTotals from '@/components/quote/QuoteTotals';
import { authErrorMessage } from '@/lib/auth';
import type { QuoteDraft, QuoteLineItem } from '@/types/Quote';

export default function Quote() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const lines = useCartStore((s) => s.lines);
  const currency = useCartStore((s) => s.currency);
  const setCurrency = useCartStore((s) => s.setCurrency);
  const updateLine = useCartStore((s) => s.updateLine);
  const removeLine = useCartStore((s) => s.removeLine);
  const clear = useCartStore((s) => s.clear);

  const [customerName, setCustomerName] = useState('');
  const [customerProject, setCustomerProject] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<{ id: string; number: string } | null>(null);

  const totals = useMemo(() => calculateTotals(lines), [lines]);
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  const previewNumber = lastSaved?.number ?? `SWC-${new Date().getFullYear()}-DRAFT`;

  function buildDraft(quoteId: string, number: string): QuoteDraft | null {
    if (!user) return null;
    const lineItems: QuoteLineItem[] = lines.map((l) => ({
      partId: l.partId,
      partSnapshot: {
        title: l.snapshot.title,
        sku: l.snapshot.sku,
        ...(l.snapshot.casting !== undefined ? { casting: l.snapshot.casting } : {}),
        iconId: l.snapshot.iconId,
      },
      qty: l.qty,
      unitPrice: l.unitPrice,
      shippingWeight: l.shippingWeight,
    }));
    return {
      id: quoteId,
      userId: user.uid,
      number,
      status: 'draft',
      customer: {
        name: customerName.trim() || 'Unnamed Customer',
        ...(customerProject.trim() ? { project: customerProject.trim() } : {}),
        ...(customerEmail.trim() ? { email: customerEmail.trim() } : {}),
      },
      lineItems,
      currency,
      subtotal: totals.subtotal,
      shipping: totals.shipping,
      tax: totals.tax,
      total: totals.total,
      taxRate: DEFAULT_QUOTE_CONFIG.taxRate,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    };
  }

  async function handleSaveDraft(): Promise<{ id: string; number: string } | null> {
    if (!user) return null;
    if (lines.length === 0) {
      setError('Add at least one line item before saving.');
      return null;
    }
    setError(null);
    setSaving(true);
    try {
      const number = await nextQuoteNumber(user.uid);
      const id = newQuoteId();
      const draft = buildDraft(id, number);
      if (!draft) return null;
      await saveQuote(draft);
      setLastSaved({ id, number });
      return { id, number };
    } catch (err) {
      setError(authErrorMessage(err));
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function handlePreviewPdf() {
    let saved = lastSaved;
    if (!saved) {
      saved = await handleSaveDraft();
    }
    if (saved) {
      navigate(`/quote/pdf?id=${encodeURIComponent(saved.id)}`);
    }
  }

  async function handleSend() {
    if (!customerEmail.trim()) {
      setError('Customer email is required to send.');
      return;
    }
    setSending(true);
    setError(null);
    try {
      let saved = lastSaved;
      if (!saved) {
        saved = await handleSaveDraft();
      }
      if (!saved) return;
      const subject = encodeURIComponent(`Slightly Wicked Customs · Quote ${saved.number}`);
      const lineBlock = lines
        .map((l) => `· ${l.snapshot.title} (${l.snapshot.sku}) — qty ${l.qty} @ $${l.unitPrice}`)
        .join('\n');
      const bodyLines = [
        `${customerName.trim() || 'Hi'},`,
        '',
        `Attached is quote ${saved.number} from Slightly Wicked Customs.`,
        '',
        'Summary:',
        lineBlock,
        '',
        `Subtotal: $${totals.subtotal.toFixed(2)}`,
        `Shipping (${totals.totalWeight.toFixed(1)} lbs): $${totals.shipping.toFixed(2)}`,
        `Tax: $${totals.tax.toFixed(2)}`,
        `Grand total: $${totals.total.toFixed(2)} ${currency}`,
        '',
        'Please download the branded PDF from the quote preview and attach it to this email before sending. (Phase 1 uses mailto: — automated PDF email lands in Phase 2.)',
        '',
        '— SWC',
      ];
      const body = encodeURIComponent(bodyLines.join('\n'));
      window.location.href = `mailto:${customerEmail.trim()}?subject=${subject}&body=${body}`;
    } finally {
      setSending(false);
    }
  }

  if (lines.length === 0) {
    return (
      <div className="app">
        <div className="main">
          <div className="eyebrow">Quote Builder</div>
          <h1 className="h-title">Empty Quote</h1>
          <div
            className="empty-state"
            style={{ background: 'var(--bg-1)', border: '1px dashed var(--line-2)', padding: 80, marginTop: 24 }}
          >
            <div style={{ fontFamily: 'var(--display)', fontSize: 36, color: 'var(--text-dim)', marginBottom: 12 }}>
              No line items yet.
            </div>
            <p style={{ marginBottom: 24 }}>Browse the inventory and click "＋ Quote" on any part.</p>
            <Link to="/inventory" className="btn">
              Browse Inventory →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="main">
        <div className="eyebrow">Quote Builder · Editable</div>
        <h1 className="h-title">Build a Quote</h1>

        <div className="quote-header">
          <div>
            <div className="quote-id">QUOTE # {previewNumber}</div>
            <div className="quote-customer">
              <label>
                <span className="lbl">Customer</span>
                <input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Anderson Restorations"
                  style={{ width: 240 }}
                />
              </label>
              <label>
                <span className="lbl">Project</span>
                <input
                  value={customerProject}
                  onChange={(e) => setCustomerProject(e.target.value)}
                  placeholder="1970 Cuda Hemi — Build #14"
                  style={{ width: 280 }}
                />
              </label>
              <label>
                <span className="lbl">Email</span>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="customer@example.com"
                  style={{ width: 240 }}
                />
              </label>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span className="badge live">{lastSaved ? 'Saved' : 'Draft'}</span>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.15em', marginTop: 8 }}>
              {today}
            </div>
          </div>
        </div>

        <table className="line-table cards-on-mobile">
          <thead>
            <tr>
              <th style={{ width: '40%' }}>Part</th>
              <th style={{ width: 60, textAlign: 'right' }}>Qty</th>
              <th style={{ width: 120, textAlign: 'right' }}>Unit Price (USD)</th>
              <th style={{ width: 100, textAlign: 'right' }}>Wt (lbs)</th>
              <th style={{ width: 130, textAlign: 'right' }}>Subtotal</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <LineItemRow
                key={line.partId}
                line={line}
                currency={currency}
                onUpdate={(patch) => updateLine(line.partId, patch)}
                onRemove={() => removeLine(line.partId)}
              />
            ))}
          </tbody>
        </table>

        <div className="add-line-row" onClick={() => navigate('/inventory')}>
          ＋ Add Another Line Item
        </div>

        <QuoteTotals totals={totals} currency={currency} onCurrency={(c) => setCurrency(c)} />

        <label style={{ display: 'block', marginBottom: 18 }}>
          <span className="auth-label">Notes (internal)</span>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Internal notes — won't be on the PDF."
            style={{ width: '100%', resize: 'vertical', fontFamily: 'var(--body)' }}
          />
        </label>

        {error && <div className="auth-error" style={{ marginBottom: 18 }}>{error}</div>}
        {lastSaved && (
          <div className="auth-success" style={{ marginBottom: 18 }}>
            <strong>Saved as draft: {lastSaved.number}</strong>
            <p>Re-save to capture further edits, or preview the branded PDF below.</p>
          </div>
        )}

        <div className="quote-actions">
          <button type="button" className="btn dark" onClick={clear} disabled={saving || sending}>
            Clear Quote
          </button>
          <button type="button" className="btn dark" onClick={handleSaveDraft} disabled={saving || sending}>
            {saving ? 'Saving…' : 'Save Draft'}
          </button>
          <button type="button" className="btn ghost" onClick={handlePreviewPdf} disabled={saving || sending}>
            Preview PDF →
          </button>
          <button type="button" className="btn" onClick={handleSend} disabled={saving || sending}>
            {sending ? 'Opening…' : 'Send to Customer →'}
          </button>
        </div>
      </div>
    </div>
  );
}
